import * as React from 'react';
import { useTranslations } from 'next-intl';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Button } from '@mentimeter/ragnar-ui/button';
import { Heading, P } from '@mentimeter/ragnar-ui/typography';
import { Link } from '@mentimeter/ragnar-ui/link';

interface CompanyContactInfoT {
  companyContactEmail?: string | undefined;
  companyContactUrl?: string | undefined;
  companyContactName?: string | undefined;
  companyName: string;
  loginUrl: string;
}
interface PropsT extends CompanyContactInfoT {
  resetForm: () => void;
}

const CompanyLink = (companyContactInfo: CompanyContactInfoT) => () => {
  const { companyContactEmail, companyContactUrl, companyContactName } =
    companyContactInfo;
  if (companyContactEmail) {
    return (
      <Link href={`mailto:${companyContactEmail}`}>{companyContactEmail}</Link>
    );
  }
  if (companyContactUrl) {
    return (
      <Link href={companyContactUrl} target="blank" rel="noopener noreferrer">
        {/* eslint-disable-next-line menti-react-jsx/no-possibly-undefined-in-jsx */}
        {companyContactName}
      </Link>
    );
  }

  // Fallback to empty link if no email or URL is provided
  return <Link href="/"></Link>;
};

const BodyMessage = (companyContactInfo: CompanyContactInfoT) => {
  const t = useTranslations('authentication.strict_sso_info');
  const { loginUrl, companyContactEmail, companyContactUrl } =
    companyContactInfo;
  return companyContactEmail || companyContactUrl ? (
    <P fontSize={[2, 3]} mb={[2, 4]} textAlign="center">
      {t.rich('request_license_message', {
        companyName: companyContactInfo.companyName,
        Link: CompanyLink(companyContactInfo),
      })}
    </P>
  ) : (
    <Button href={loginUrl} mb={4} variant="primary">
      {t('sign_up_sso_button')}
    </Button>
  );
};

const PersonalAccountLink =
  (onClick: React.MouseEventHandler<HTMLAnchorElement>) =>
  (chunks: React.ReactNode) => (
    <Link href="#" onClick={onClick}>
      {chunks}
    </Link>
  );
const StrictSSOInfo = ({
  resetForm,
  companyName,
  loginUrl,
  companyContactEmail,
  companyContactUrl,
  companyContactName,
}: PropsT) => {
  const t = useTranslations('authentication.strict_sso_info');
  const companyContactInfo: CompanyContactInfoT = {
    loginUrl,
    companyContactEmail,
    companyContactName,
    companyContactUrl,
    companyName,
  };

  const onLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    resetForm();
  };

  return (
    <Box width="100%" alignItems="stretch">
      <Heading textAlign="center" fontSize={4} color="inherit">
        {t.rich('company_agreement_heading', { companyName })}
      </Heading>
      <P as="p" fontSize={[2, 3]} mb={[2, 4]} textAlign="center">
        {t('sign_up_sso_message')}
      </P>
      <BodyMessage {...companyContactInfo} />
      <Box
        p={3}
        bg="neutralWeakest"
        borderRadius={2}
        alignItems="center"
        justifyContent="center"
      >
        <P textAlign="center" mb={0}>
          {t.rich('alternative_email_message', {
            Link: PersonalAccountLink(onLinkClick),
          })}
        </P>
      </Box>
    </Box>
  );
};

export default StrictSSOInfo;
